import { type RENDER_MODES } from '../../../shared/constants';
import {
  type ActiveFiltersState,
  type ActiveGraphPointState,
  type AdvancedFilterState,
  type InsightItemTypes,
  type ListRowState,
  type TabFilterState,
} from '../../../shared/types';
import { type FilterCacheState } from '../hooks';

export enum ViolationsTabType {
  SLICE_NAME = 'aag/violationsTab',
  API_SLICE_NAME = 'aag/violationsTabApi',
}

export interface ColumnState {
  label: string;
  value: string;
  isChecked: boolean;
  isDisabled: boolean;
}

export type FilterDeltaState = Record<
  'FILTERS_ADDED' | 'FILTERS_CLEANED',
  ActiveFiltersState[]
>;

export type SortState = Record<'value' | 'order', string> | null;

export type RenderModeState = (typeof RENDER_MODES)[keyof typeof RENDER_MODES];

interface ViolationsTabControlsState {
  isInsightsOpen: boolean;
  renderMode: RenderModeState;
  tabFilter: TabFilterState[];
  filters: AdvancedFilterState[];
  activeFilters: ActiveFiltersState[];
  filtersDelta: FilterDeltaState;
  tabInAction: string | null;
  sortInAction: SortState;
  activeColumns: ColumnState[];
  filterCache: FilterCacheState;
  activeInsight: InsightItemTypes | null;
  searchKey: string | null;
  activeGraphPoint: ActiveGraphPointState | null;
}

export interface ViolationsTabListState {
  isListLoading: boolean;
  totalCount: number | null;
  rowIdToTransition: number | string | null;
}

interface ViolationsTabBulkActionState {
  isFooterVisible: boolean;
  bulkCount: number;
  isBulkChecked: boolean;
}

export interface ViolationsTabSidePanelRowContextState {
  id: string;
  title?: string;
  displayName?: string;
  username?: string;
  isExpiredCount?: number;
  isExpiringSoonCount?: number;
  mitigatingControlCount?: number;
}

export interface ViolationsTabSidePanelGroupRowContextState {
  current: ViolationsTabSidePanelRowContextState;
  prev?: ViolationsTabSidePanelRowContextState;
  next?: ViolationsTabSidePanelRowContextState;
}

interface ViolationsTabSidePanelState {
  isSidePanelVisible: boolean;
  rowContext: ViolationsTabSidePanelGroupRowContextState;
}

export interface ViolationsTabMitigateModalDataState {
  rowId: string;
}

interface ViolationsTabModalIslandState {
  isMitigateModalOpen: boolean;
  mitigateModalData: ViolationsTabMitigateModalDataState;
  isRemediateModalOpen: boolean;
  isReviewModalOpen: boolean;
}

export interface ViolationsTabState {
  isLoading: boolean;
  controls: ViolationsTabControlsState;
  list: ViolationsTabListState;
  bulkaction: ViolationsTabBulkActionState;
  sidepanel: ViolationsTabSidePanelState;
  modalIsland: ViolationsTabModalIslandState;
}

export interface GetFilterOptionsParams {
  searchText: string;
  range: number;
}

// Query inputs
export interface FiltersGeneralProps {
  max?: string;
  offset?: string;
  search?: string;
  fields?: string;
  sort?: string;
  order?: string;
  key?: string;
  isValue?: boolean;
}

export interface SodViolationsListProps {
  filters: {
    sodStatuses: string[];
    risk: {
      riskIds?: number[];
      riskName?: string[];
      actualRisk?: string[];
      riskPriority?: string[];
      riskType?: string[];
      businessProcess?: string[];
    };
    account?: {
      accountKeys?: number[];
      accountType?: string[];
      userLock?: string[];
    };
    ruleset?: {
      rulesetKeys?: number[];
    };
    application?: {
      endpointKeys?: number[];
      applicationName?: string[];
    };
    user?: {
      userKeys?: number[];
      userName?: string[];
      firstName?: string[];
      lastName?: string[];
      costCenter?: string[];
      employeeId?: string[];
      jobCode?: string[];
    };
    function?: {
      functionKeys?: number[];
      status?: string[];
      functionName?: string[];
    };
    entitlement?: {
      entitlementValueKeys?: number[];
      entitlementType?: string[];
      value?: string[];
    };
    mitigatingControl?: {
      mitigatingControlKeys?: number[];
      mitigatingControlName?: string[];
      mcExpiryDate?: {
        startDate?: string | null;
        endDate?: string | null;
      };
      mcAssignedStartDate?: {
        startDate?: string | null;
        endDate?: string | null;
      };
      mcAssignedEndDate?: {
        startDate?: string | null;
        endDate?: string | null;
      };
    };
    globalSearch?: string;
    insightRequests?: Pick<InsightItemTypes, 'id' | 'filters'>[];
  };
  pagination: {
    offset: number;
    max: number;
  };
  sort: {
    field: string;
    order: string;
  };
}

export interface GetUserDetailsProps {
  usernameQuery: string;
}

// API responses
interface GetApiResponse {
  status?: number;
  errorCode?: string;
  displayRecords?: number;
  totalRecords?: number;
  displayCount?: number;
  displaycount?: number;
}

export interface GetTabCountApiResponse {
  openCount: number;
  mitigatedCount: number;
  closedCount: number;
  inReviewCount: number;
  remediatedCount: number;
}

export interface ColumnPickerState {
  id: string;
  localizationKey: string;
  isDefault: boolean;
}

export interface GetColumnPickerApiResponse {
  status: number;
  data: Record<string, ColumnPickerState[]>;
}

export interface GetRiskApiResponse extends GetApiResponse {
  data?: {
    riskId: number;
    riskName: string;
  }[];
}

export interface GetBusinessProcessApiResponse extends GetApiResponse {
  data?: {
    businessProcessKey: number;
    businessProcess: string;
  }[];
}

export interface GetFunctionsApiResponse extends GetApiResponse {
  data?: {
    id: number;
    functionName: string;
  }[];
}

export interface GetRulesetsApiResponse extends GetApiResponse {
  data?: {
    id: number;
    rulesetName: string;
  }[];
}

export interface GetApplicationEndpointApiResponse extends GetApiResponse {
  endpoints?: {
    id: string;
    displayName: string;
  }[];
}

export interface GetUserFiltersApiResponse extends GetApiResponse {
  userlist?: {
    firstname: string;
    lastname: string;
    username: string;
    userKey: string;
    costcenter: string;
  }[];
}

export interface GetAccountsApiResponse extends GetApiResponse {
  Accountdetails?: {
    name: string;
    accountKey: string;
  }[];
}

export interface GetEntitlementsApiResponse extends GetApiResponse {
  entitlementsCount?: number;
  Entitlementdetails?: {
    entitlement_valuekey: string;
    entitlement_value: string;
  }[];
}

export interface GetEntitlementTypessApiResponse extends GetApiResponse {
  entitlementTypeDetails?: {
    displayName: string;
    entitlementTypeKey: number;
  }[];
}

export interface GetMitigatingControlsApiResponse extends GetApiResponse {
  data?: {
    mitigatingControlId: number;
    mitigatingControl: string;
  }[];
}

export interface GetInsightDetailsApiResponse extends GetApiResponse {
  data?: {
    id: number;
    title: string;
    filters: object;
    description: string;
  };
}

export interface GetViolationsGraphResponse extends GetApiResponse {
  data?: {
    clusters: ActiveGraphPointState[];
    maxViolationAge: number;
    maxViolationScore: number;
    totalViolations: number;
    timeRange: string;
    updatedTime: string;
  };
}

export interface GetViolationsGraphClusterResponse extends GetApiResponse {
  data?: ListRowState[];
  page: { max: number; offset: number };
  sort: { order: string; columnToSort: string };
}
